import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Theme } from '@/types';

interface LayoutState {
  menuOpen: boolean;
  menuMode: 'vertical' | 'horizontal';
  theme: Theme;
  loading: boolean;
}

interface LayoutActions {
  toggleMenu: () => void;
  setMenuOpen: (open: boolean) => void;
  setMenuMode: (mode: 'vertical' | 'horizontal') => void;
  setTheme: (theme: Theme) => void;
  setLoading: (loading: boolean) => void;
}

interface LayoutStore extends LayoutState, LayoutActions {}

/**
 * Layout Store
 * Manages layout state, menu visibility, theme, and loading states
 */
export const useLayoutStore = create<LayoutStore>()(
  persist(
    (set) => ({
      // State
      menuOpen: true,
      menuMode: 'vertical',
      theme: 'light',
      loading: false,

      // Actions
      toggleMenu: () => {
        set((state) => ({ menuOpen: !state.menuOpen }));
      },

      setMenuOpen: (open: boolean) => {
        set({ menuOpen: open });
      },

      setMenuMode: (mode: 'vertical' | 'horizontal') => {
        set({ menuMode: mode });
      },

      setTheme: (theme: Theme) => {
        // Update CSS custom property
        document.documentElement.setAttribute('data-theme', theme);
        
        // Update localStorage
        localStorage.setItem('alz-theme', theme);
        
        // Update store
        set({ theme });
      },

      setLoading: (loading: boolean) => {
        set({ loading });
      },
    }),
    {
      name: 'alz-layout-storage',
      partialize: (state) => ({
        menuOpen: state.menuOpen,
        menuMode: state.menuMode,
        theme: state.theme,
      }),
      onRehydrateStorage: () => (state) => {
        // Apply theme on rehydration
        if (state?.theme) {
          document.documentElement.setAttribute('data-theme', state.theme);
        }
      },
    }
  )
);

// Selector hooks for better performance
export const useLayout = () => useLayoutStore((state) => ({
  menuOpen: state.menuOpen,
  menuMode: state.menuMode,
  theme: state.theme,
  loading: state.loading,
}));

export const useLayoutActions = () => useLayoutStore((state) => ({
  toggleMenu: state.toggleMenu,
  setMenuOpen: state.setMenuOpen,
  setMenuMode: state.setMenuMode,
  setTheme: state.setTheme,
  setLoading: state.setLoading,
}));